/**
 * Lane 3A (Feb 2026) — Soft browser marker.
 *
 * Records that this browser has already taken its free-lesson taste
 * so returning visitors get a friendly "you've had your free lesson"
 * nudge instead of silently generating another.
 *
 * DELIBERATE UX-only signal — NOT a server-enforced gate. There is
 * no email, no login, and no IP throttle behind this: a visitor who
 * clears storage or opens incognito can absolutely get another
 * taste. The cookie-clearer is not the target user; the honest
 * majority is.
 *
 * Shape mirrors `teacher.js` (26-line canonical template):
 * safe against missing window (SSR), silently ignores storage
 * disabled, `_v1` key suffix for future migrations without stale
 * marker weirdness.
 */

const STORAGE_KEY = "atomicedu_free_lesson_claimed_v1";

const _read = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
};

export const hasClaimedFreeLesson = () => {
  if (typeof window === "undefined") return false;
  return _read() !== null;
};

export const getClaimedFreeLesson = () => {
  if (typeof window === "undefined") return null;
  return _read();
};

/** Persist a successful claim. `token` must be truthy — do NOT set
 *  on a failed generation. */
export const setFreeLessonClaimed = (token) => {
  if (typeof window === "undefined" || !token) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ token, claimed_at: new Date().toISOString() }),
    );
  } catch (_e) {
    /* storage disabled — nothing to do */
  }
};

export const clearFreeLessonMarker = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    /* ignore */
  }
};
